<script setup lang="ts">
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmDrawer,
  TirPmDrawerPositionEnum,
  TirPmIconView,
  TirPmScrollbar,
} from 'tir-components';
import { computed, PropType, ref } from 'vue';
import UserModel from '../../models/UserModel';
import DailySummariesModel from '../../models/DailySummariesModel';
import { OrderTaskModel, TaskColumnModel } from '../../models';
import { TaskViewElementEnum } from '../../enums';
import { TaskDrawerSettingsModel, TaskDrawer } from '@/components';
import { CrossLinedIcon } from 'tir-style-system/icons/outline';
import { TaskCardMonitoring } from '../../components/task-card-monitoring';
import { TaskHeader } from './task-header';
import { ProjectInfoModel } from './task-header/models';
import { ProjectModel } from '@/models';

const props = defineProps({
  /**Видимость tir-pm-drawer */
  visibleDrawer: { type: Boolean },
  /**Данные юзера выбранной задачи  */
  selectUserData: { type: Object as PropType<UserModel> },
  /**Данные сводок выбраной задачи  */
  selectDailySummaries: { type: Object as PropType<DailySummariesModel> },
  /**Список задач */
  tasks: {
    type: Array as PropType<OrderTaskModel[]>,
  },
  /**Колонки */
  columns: { type: Array as PropType<TaskColumnModel[]> },
  /**Итоговое время юзера */
  timeSpentUser: { type: Number },
});

const emit = defineEmits<{
  /**Событие закрытия шторки */
  (e: 'EventCloseDrawer');
  /**Событие открытия шторки */
  (e: 'EventOpenDrawer');
  /**Событие обновления видимости дравера */
  (e: 'EventonUpdateVisibleDrawer');
}>();

/**Ссылка на компонент шторки задач */
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();

/**Настройки видимости */
const viewSettings = ref([
  TaskViewElementEnum.Namespace,
  TaskViewElementEnum.Queue,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
]);

/**Список проектов с задачами */
const projectList = computed(() => {
  const result: ProjectInfoModel[] = [];

  props.tasks.forEach((task) => {
    if (!task.Project) return;

    const existing = result.find((p) => p.Project.Id === task.ProjectId);

    if (existing) {
      existing.Tasks.push(task);
      existing.TotalWorkTime += task.WorkTime;
    } else {
      result.push(
        new ProjectInfoModel({
          Project: task.Project,
          TotalWorkTime: task.WorkTime,
          Tasks: [task],
        }),
      );
    }
  });

  return result;
});

/**Получить день недели */
const getDayWeek = (date: Date) => {
  let newDate = new Date(date);
  newDate.setDate(newDate.getDate());

  const dayOfWeek = newDate.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  return {
    day: newDate.toLocaleDateString('ru-RU', { weekday: 'short' }).toUpperCase(),
    isWeekend,
  };
};

/**Форматирование даты */
const formatDate = (date: Date) => {
  let newDate = new Date(date);
  newDate.setDate(newDate.getDate());

  const dayOfWeek = getDayWeek(date);
  return (
    newDate.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + ` (${dayOfWeek.day})`
  );
};

/**Закрыть дравер */
const closeDrawer = () => {
  emit('EventCloseDrawer');
};

/** Форматирование времени */
const formatTime = (time: number): string => {
  console.log(time);
  
  const hours = Math.floor(time);
  const minutes = Math.round((time - hours) * 100);

  const extraHours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const totalHours = hours + extraHours;

  const formattedHours = totalHours.toString().padStart(2, '0');
  const formattedMinutes = remainingMinutes.toString().padStart(2, '0');

  return `${formattedHours}ч ${formattedMinutes}м`;
};

/**Открыть шторку с задачей */
const openTaskDrawer = (taskId: number) => {
  emit('EventCloseDrawer');
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'edit',
      TaskId: taskId,
    }),
  );
};

/**Открыть предыдущую шторку */
const openTaskDrawerBack = () => {
  $taskDrawer.value?.close();
  emit('EventOpenDrawer');
};
</script>
<template>
  <TirPmDrawer
    :visible="visibleDrawer"
    :position="TirPmDrawerPositionEnum.Right"
    panelClass="monitoring-days-drawer-tasks__drawer"
    overlayClass="monitoring-days-drawer-tasks__drawer"
    @update:visible="emit('EventonUpdateVisibleDrawer')"
  >
    <template #content>
      <div class="monitoring-days-drawer-tasks__drawer-content">
        <div class="monitoring-days-drawer-tasks__drawer-content-block">
          <div class="monitoring-days-drawer-tasks__drawer-content-user">
            <TirPmIconView
              :size="32"
              isRounded
              :isBordered="false"
              :colorCode="selectUserData?.AvatarColor"
              :imageLoadUrl="selectUserData?.Foto"
            >
              <template #mainSlot>
                {{ selectUserData?.LastName[0] }}{{ selectUserData?.FirstName[0] }}
              </template>
            </TirPmIconView>
            <div class="monitoring-days-drawer-tasks__drawer-content-user-data">
              {{ selectUserData?.LastName }} {{ selectUserData?.FirstName }}
              {{ selectUserData?.MiddleName }} -
              <span class="monitoring-days-drawer-tasks__drawer-content-user-date">
                {{ formatDate(selectDailySummaries?.Date) }}</span
              >
            </div>
          </div>
          <TirPmButton
            :size="TirPmButtonSizeEnum.Regular"
            :variant="TirPmButtonVariantEnum.LinkState"
            @click="closeDrawer"
          >
            <template #leftSlot>
              <CrossLinedIcon />
            </template>
          </TirPmButton>
        </div>
        <TirPmScrollbar showOnHover class="monitoring-days-drawer-tasks__scrollbar">
          <div class="monitoring-days-drawer-tasks__project-list">
            <div v-for="item in projectList" :key="item.Project.Id">
              <div class="monitoring-days-drawer-tasks__top-info-block">
                <div class="monitoring-days-drawer-tasks__project-info">
                  <TirPmIconView
                    :size="32"
                    isRounded
                    :isBordered="false"
                    :colorCode="item.Project?.PreviewColor"
                    :imageLoadUrl="item.Project?.ImageUrl"
                  >
                    <template #mainSlot>
                      {{ item.Project?.Name[0] }}
                    </template>
                  </TirPmIconView>
                  <div class="monitoring-days-drawer-tasks__project-name">
                    {{ item.Project.Name }}
                  </div>
                </div>
                <div class="monitoring-days-drawer-tasks__total-time-block">
                  <div class="monitoring-days-drawer-tasks__total-time">
                    ИТОГО: {{ formatTime(item.TotalWorkTime) }}
                  </div>
                </div>
              </div>
              <TaskHeader
                class="monitoring-days-drawer-tasks__header-table"
                :columns="columns"
                :view-settings="viewSettings"
              />
              <div class="monitoring-days-drawer-tasks__drawer-tickets">
                <TaskCardMonitoring
                  v-for="task in item.Tasks"
                  :key="task.Id"
                  :task="task"
                  :columns="columns"
                  :view-settings="viewSettings"
                  hideOpenAndDelete
                  @open-task-drawer="openTaskDrawer(task.Id)"
                />
              </div>
              <div v-if="projectList.length > 1" class="monitoring-days-drawer-tasks__line" />
            </div>
          </div>
        </TirPmScrollbar>
      </div>
    </template>
  </TirPmDrawer>
  <TaskDrawer
    ref="$taskDrawer"
    showBackButton
    @onBack="openTaskDrawerBack"
    panelClass="monitoring-days-drawer-tasks__drawer"
  />
</template>
<style scoped lang="scss">
.monitoring-days-drawer-tasks {
  &__line {
    width: 100%;
    border-bottom: 0.0313rem solid var(--text-tertiary, #93969b);
  }
  &__drawer-content {
    padding: 2.5rem;
  }
  &__drawer-content-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2.75rem;
  }
  &__scrollbar {
    max-height: calc(100vh - 9rem);
  }
  &__project-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  &__project-name {
    font-weight: 500;
    font-size: 0.875rem;
  }
  &__drawer-content-user-date {
    color: var(--text-secondary, #686c73);
  }
  &__project-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__top-info-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
  &__header-table {
    margin-bottom: 1rem;
  }
  &__drawer-content-user {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  &__drawer-content-user-data {
    display: flex;
    align-items: center;
    font-size: 1.625rem;
    font-weight: 500;
  }

  &__drawer-tickets {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  &__total-time-block {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
  }

  &__total-time {
    border: 0.125rem solid var(--text-secondary, #686c73);
    border-radius: 0.5rem;
    font-size: 1.125rem;
    font-weight: 500;
    padding: 0.25rem 0.5rem 0.25rem 0.5rem;
    color: var(--text-secondary, #686c73);
  }
}
</style>

<style lang="scss">
.monitoring-days-drawer-tasks {
  &__drawer {
    z-index: 1000 !important;
  }
}
.monitoring-days-drawer-tasks__drawer-tickets .tir-pm-context-menu__target .task-card_left {
  display: none !important;
}
</style>
